/**
 * Dashboard Stats Component for v0.7.0
 * Displays key statistics for admin dashboard
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DashboardStats as DashboardStatsType } from '../../types';
import { getDashboard } from '../../services/dashboardApi';
import './DashboardStats.css';

interface DashboardStatsProps {
  className?: string
}

const DashboardStats = ({ className = '' }: DashboardStatsProps) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStatsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
    // Refresh stats every 30 seconds
    const interval = setInterval(loadStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    try {
      const response = await getDashboard();
      setStats(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`dashboard-stats dashboard-stats--loading ${className}`}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`dashboard-stats dashboard-stats--error ${className}`}>
        <p className="error-message">{error}</p>
        <button onClick={loadStats} className="btn btn--sm">重試</button>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div className={`dashboard-stats ${className}`}>
      <div className="dashboard-stats__grid">
        {/* Members Section */}
        <div className="dashboard-stats__column">
          <h3 className="dashboard-stats__column-title">會員統計</h3>
          <div className="dashboard-stats__metric">
            <div className="dashboard-stats__metric-label">本週新增</div>
            <div className="dashboard-stats__metric-value">+{stats.members.newThisWeek}</div>
          </div>
          <div className="dashboard-stats__metric">
            <div className="dashboard-stats__metric-label">總會員數</div>
            <div className="dashboard-stats__metric-value">{stats.members.total.toLocaleString()}</div>
          </div>
        </div>

        {/* Ads Section */}
        <div className="dashboard-stats__column">
          <h3 className="dashboard-stats__column-title">廣告統計</h3>
          <div className="dashboard-stats__metric dashboard-stats__metric--pending">
            <div className="dashboard-stats__metric-label">待審核</div>
            <div className="dashboard-stats__metric-value">{stats.ads.pending}</div>
          </div>
          <div className="dashboard-stats__metric">
            <div className="dashboard-stats__metric-label">總廣告數</div>
            <div className="dashboard-stats__metric-value">{stats.ads.total.toLocaleString()}</div>
          </div>
        </div>

        {/* Wishes Section */}
        <div className="dashboard-stats__column">
          <h3 className="dashboard-stats__column-title">許願統計</h3>
          <div className="dashboard-stats__metric">
            <div className="dashboard-stats__metric-label">本週新增</div>
            <div className="dashboard-stats__metric-value">+{stats.wishes.newThisWeek}</div>
          </div>
          <div className="dashboard-stats__metric">
            <div className="dashboard-stats__metric-label">總許願數</div>
            <div className="dashboard-stats__metric-value">{stats.wishes.total.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Pending Approvals Alert */}
      {stats.pendingApprovals.ads > 0 && (
        <div className="dashboard-stats__alert">
          <div className="alert-icon">⚠️</div>
          <div className="alert-content">
            <div className="alert-title">待審核項目</div>
            <div className="alert-description">
              {stats.pendingApprovals.ads} 則廣告等待審核
            </div>
          </div>
          <button 
            className="btn btn--sm btn--primary"
            onClick={() => navigate('/admin/queue')}
          >
            前往處理
          </button>
        </div>
      )}
    </div>
  );
};

export default DashboardStats;
